/* eslint-disable no-unused-vars */
import {useState, useRef, useEffect} from "react";
import {useHistory, useParams} from "react-router-dom";
import {FieldArray, Formik} from "formik";
import {DashboardLayout} from "components/Layout";
import {convertErrorMessageFormat} from "utils/converter";
import {getPatients} from "hooks/PendaftaranPasien";
import {SubmitButton} from "components/Button";
import {useUploadFile} from "hooks/Misc";
import {useToast} from "hooks/useToast";
import {useSelector} from "react-redux";
import {DropdownAsync} from "components/Dropdown/DropdownAsync";
import {getDoctors} from "hooks/Doctor";
import RateInput from "components/Form/RateInput";
import {useDropzone} from "react-dropzone";
import * as Yup from "yup";
import {useEditReview, useDetailReview} from "hooks/AdminAdmission/Review";
import clsx from "clsx";

const FormSchemaValidation = Yup.object().shape({
	doctor_user_ids: Yup.array().required(),
	patient_user_id: Yup.object().required(),
	rating: Yup.number(),
	comment: Yup.string().nullable(),
});

const FormInitialValues = {
	doctor_user_ids: [],
	patient_user_id: null,
	attachment_urls: [],
	rating: 0,
	comment: null,
};

export function ReviewEditPage() {
	const formikRef = useRef();
	const history = useHistory();
	const {id} = useParams();
	const {mutate, isLoading} = useEditReview(id);
	const {mutateAsync: uploadFile, isLoading: isLoadingUpload} = useUploadFile();
	const {showToast} = useToast();
	const {data: reviewData, isLoading: isLoadingReview} = useDetailReview(id);

	const handleSubmit = async formValue => {
		let {attachment_urls, ...formDataValue} = formValue;

		formDataValue.patient_user_id = formDataValue.patient_user_id.value;
		formDataValue.doctor_user_ids = formDataValue.doctor_user_ids?.map(item => item.value);

		if (attachment_urls?.filter(file => file instanceof File)?.length) {
			// let formData = new FormData();

			// formData.append("file", attachment_urls[0]);
			const resultFile = await Promise.all(
				attachment_urls.map(async file => {
					const fileFormData = new FormData();

					fileFormData.append("file", file);

					const res = await uploadFile(fileFormData);

					return res.data?.file;
				}),
			);

			mutate(
				{attachment_urls: resultFile, ...formDataValue},
				{
					onSuccess: () => {
						showToast("success", "Data review berhasil ditambahkan", 3000);
						history.push("/admission/review/list");
					},
					onError: res => {
						showToast(
							"error",
							convertErrorMessageFormat(res.response.status, res.response.data.message),
							null,
						);
						window.scrollTo(0, 0);
					},
				},
			);
		} else {
			mutate(
				{...formDataValue},
				{
					onSuccess: () => {
						showToast("success", "Data review berhasil ditambahkan", 3000);
						history.push("/admission/review/list");
					},
					onError: res => {
						showToast(
							"error",
							convertErrorMessageFormat(res.response.status, res.response.data.message),
							null,
						);
						window.scrollTo(0, 0);
					},
				},
			);
		}
	};

	const fetchPatientData = async (search, callback) => {
		const response = await getPatients(null, {page: 1, search, limit: 10});
		const {rows} = response?.data ?? {};

		callback(rows.map(item => ({label: `${item.firstName} ${item.lastName}`, value: item.id})));
	};
	const [defaultPatientOpts, setDefaultPatientOpts] = useState([]);

	useEffect(() => {
		const getDefaultPatientOpts = async () => {
			const response = await getPatients(null, {page: 1, limit: 10});
			const {rows} = response?.data ?? {};

			setDefaultPatientOpts(rows.map(item => ({label: `${item.firstName} ${item.lastName}`, value: item.id})));
		};

		if (defaultPatientOpts.length === 0) {
			getDefaultPatientOpts();
		}
	}, []);

	const fetchDoctorsData = async (search, callback) => {
		const response = await getDoctors(null, {page: 1, search, limit: 10});
		const {rows} = response?.data ?? {};

		callback(rows.map(item => ({label: `${item.firstName} ${item.lastName}`, value: item.id})));
	};
	const [defaultDoctorOpts, setDefaultDoctorOpts] = useState([]);

	useEffect(() => {
		const getDefaultDoctorOpts = async () => {
			const response = await getDoctors(null, {page: 1, limit: 10});
			const {rows} = response?.data ?? {};

			setDefaultDoctorOpts(rows.map(item => ({label: `${item.firstName} ${item.lastName}`, value: item.id})));
		};

		if (defaultDoctorOpts.length === 0) {
			getDefaultDoctorOpts();
		}
	}, []);

	const {getRootProps, getInputProps} = useDropzone({
		accept: {
			"image/jpeg": [],
			"image/png": [],
		},
		multiple: true,
		maxFiles: 3,
		onDropAccepted: files => {
			formikRef.current.setFieldValue("attachment_urls", files);
		},
		onDropRejected: res => {
			formikRef.current.setFieldError("attachment_urls", "Hanya menerima format .jpeg, .png");
		},
	});

	const handleUploadFile = e => {
		const files = e.target.files;

		formikRef.current.setFieldValue("attachment_urls", files);
	};

	const [initialFormValues, setInitialFormValues] = useState(null);

	useEffect(() => {
		if (reviewData?.success) {
			const {patient, doctors, attachments, rating, comment} = reviewData.data;

			setInitialFormValues({
				patient_user_id: {
					label: `${patient.firstName} ${patient.lastName}`,
					value: patient.id,
				},
				doctor_user_ids: doctors.map(item => ({
					label: `${item.firstName} ${item.lastName}`,
					value: item.id,
				})),
				attachment_urls: attachments?.map(item => item.attachmentUrl) || [],
				rating: +rating || 0,
				comment: comment || null,
			});
		}
	}, [reviewData]);

	if (isLoadingReview || initialFormValues === null) {
		return <div className="text-center">Loading...</div>;
	}

	return (
		<DashboardLayout>
			<Formik
				enableReinitialize
				initialValues={initialFormValues}
				innerRef={formikRef}
				validationSchema={FormSchemaValidation}
				onSubmit={handleSubmit}>
				{({handleSubmit, values, errors, setFieldValue, touched, handleChange, handleBlur}) => (
					<div class="box-white setting-content h-100 no-border p24">
						<div class="d-flex justify-content-between flex-wrap align-items-center mb-4">
							<div class="ttl-20">Edit Review</div>
						</div>

						<hr />

						<div class="text-bold mb-4">
							<a
								class="text-dark"
								style={{cursor: "pointer"}}
								onClick={() => history.push("/admission/review/list")}>
								<span class="icon-ico-back me-2" />
							</a>
							Daftar List Review
						</div>

						<div class="card-body">
							<div class="row gap-4 mb-5">
								<div class="col-sm-12 col-12">
									<div>
										<label className="setting-label required">Pasien</label>
										<DropdownAsync
											disabled
											defaultOptions={defaultPatientOpts}
											defaultValue={values.patient_user_id}
											fetchOptions={fetchPatientData}
											isError={errors.patient_user_id}
											isMulti={false}
											name="patient_user_id"
											onChange={fieldValue => {
												setFieldValue("patient_user_id", fieldValue);
											}}
										/>
										{errors.patient_user_id && touched.patient_user_id && (
											<p className="form-error-item-message">{errors.patient_user_id}</p>
										)}
									</div>
								</div>
								<div class="col-sm-12 col-12">
									<div>
										<label className="setting-label required">Dokter</label>
										<DropdownAsync
											defaultOptions={defaultDoctorOpts}
											defaultValue={values.doctor_user_ids}
											fetchOptions={fetchDoctorsData}
											isError={errors.doctor_user_ids}
											isMulti={true}
											name="doctor_user_ids"
											onChange={fieldValue => {
												setFieldValue("doctor_user_ids", fieldValue);
											}}
										/>
										{errors.doctor_user_ids && touched.doctor_user_ids && (
											<p className="form-error-item-message">{errors.doctor_user_ids}</p>
										)}
									</div>
								</div>
								<div className="col-sm-12 col-12">
									<RateInput
										initialRating={values.rating}
										onChange={value => setFieldValue("rating", value)}
									/>
								</div>
								<div class="col-sm-12 col-12">
									<label className="setting-label">Masukkan gambar</label>
									<div
										class="item-wrapper one "
										{...getRootProps({className: "dropzone"})}
										style={{cursor: "pointer"}}>
										<div class="item">
											<div class="item-inner">
												<div class="item-content">
													<div class="image-upload">
														<label for="file_upload" style={{cursor: "pointer"}}>
															<img alt="" class="uploaded-image" src="" />
															<div class="h-100">
																<div class="dplay-tbl">
																	<div class="dplay-tbl-cell">
																		{/* <i class="fa fa-cloud-upload"></i> */}
																		<span className="icon-upload-cloud icon-large" />

																		<FieldArray
																			name="attachment_urls"
																			render={arrayHelpers => (
																				<h5>
																					{values.attachment_urls?.length ? (
																						<>
																							<ul>
																								{values.attachment_urls?.map(
																									(file, idx) => (
																										<li
																											key={idx}
																											className="text-start">
																											{typeof file ===
																											"string"
																												? file
																												: file.name}{" "}
																											<span
																												className="ms-2 icon-ico-delete ft-18 text-danger"
																												onClick={e => {
																													e.stopPropagation();
																													arrayHelpers.remove(
																														idx,
																													);
																												}}
																											/>
																										</li>
																									),
																								)}
																							</ul>
																						</>
																					) : (
																						<b>
																							Pilih file yang akan di
																							upload
																						</b>
																					)}
																				</h5>
																			)}
																		/>
																		{!values.attachment_urls?.length && (
																			<h6 class="mt-10 mb-70 ft-14">
																				Hanya menerima format .png, .jpeg
																			</h6>
																		)}
																	</div>
																</div>
															</div>
															<input
																accept="application/pdf"
																class="image-input"
																name="attachment_urls"
																type="file"
																value=""
																onChange={handleUploadFile}
																{...getInputProps()}
															/>
														</label>
													</div>
													{errors?.attachment_urls && (
														<p className="form-error-item-message">
															{errors?.attachment_urls}
														</p>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
								<div class="col-sm-12">
									<div class="setting-item">
										<label class="setting-label">Note</label>
										<textarea
											class={clsx(
												"form-control",
												errors?.comment && touched?.comment && "form-control-error",
											)}
											name="comment"
											placeholder="Write something about ..."
											rows="5"
											spellcheck="false"
											value={values?.comment}
											onBlur={handleBlur}
											onChange={handleChange}
										/>
										{errors.comment && touched?.comment && (
											<p className="form-error-item-message">{errors.comment}</p>
										)}
									</div>
								</div>
							</div>
						</div>

						<div class="text-center my-3">
							<SubmitButton
								className="btn btn-orange d-inline-block mw-250 m-1"
								disabled={!FormSchemaValidation.isValidSync(values) || isLoading || isLoadingUpload}
								loading={isLoading || isLoadingUpload}
								text="Save"
								onClick={handleSubmit}
							/>
						</div>
					</div>
				)}
			</Formik>
		</DashboardLayout>
	);
}
